/**
 * NormalBroadCastForm コンポーネント
 *
 * 通常配信用のフォームコンポーネント。
 * 全員配信とCSVアップロードによる配信対象者指定に対応。
 * テンプレート選択、画像アップロード、送信内容設定などの機能を提供。
 *
 * @file 通常配信フォーム
 * @date 2025/09/18
 */

import React, { useState, useCallback } from 'react';
import {
  Form,
  Input,
  Button,
  Space,
  DatePicker,
  Select,
  Flex,
  Radio,
  Upload,
  UploadProps,
  message,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { BroadCastFormType } from 'src/models/broadCast';
import { useLoginAccount } from 'src/globalStates/loginAccount';
import TextArea from 'antd/es/input/TextArea';
import { useBroadCastForm } from 'src/usecases/broadCast/form';
import dayjs from 'dayjs';
import { MessageTemplate } from 'src/models/messageTemplate';
import { RcFile } from 'antd/es/upload';
import { ImageUpload } from './shared/ImageUpload';
import { TemplateSelect } from './shared/TemplateSelect';
import { useImageHandling } from './shared/useImageHandling';
import { csvToArray } from 'src/util/array';
import { download } from 'src/util/download';
import { DownloadButton } from 'src/components/ui';
import { FullscreenLoading } from 'src/components/function/loading';

/**
 * Props インターフェース
 */
interface Props {
  /** 編集時の初期値 */
  init?: BroadCastFormType;
  /** 配信タイプ切り替え時のリセットフラグ */
  deliveryTypeChanged?: boolean;
}

/**
 * NormalBroadCastForm コンポーネント
 *
 * 通常配信用のフォームコンポーネント。
 * 既存の配信機能（全員配信、CSVアップロード）を提供する。
 *
 * @param props - コンポーネントのプロパティ
 * @returns JSX.Element
 */
export const NormalBroadCastForm: React.FC<Props> = ({ init, deliveryTypeChanged }) => {
  // Hooks
  const loginAccount = useLoginAccount();
  const _formState = useBroadCastForm();

  // フォーム関連
  const [form] = Form.useForm<BroadCastFormType>();
  const type = Form.useWatch('type', form);
  const lineIds = Form.useWatch('sendMCustomerLineIds', form);

  // 状態管理
  const [loading, setLoading] = React.useState(false);
  const [processing, setProcessing] = useState(false);
  const initialRadioValue = React.useMemo(
    () => (init?.broadCast?.sendMCustomerLineIds?.length ?? 0) === 0,
    [init?.broadCast?.sendMCustomerLineIds]
  );
  const [radioValue, setRadioValue] = useState(initialRadioValue);

  // CSVファイル読み込み用
  const readerRef = React.useRef<FileReader | null>(null);

  React.useEffect(() => {
    readerRef.current = new FileReader();
    return () => {
      if (readerRef.current) {
        readerRef.current.abort();
      }
    };
  }, []);

  // 送信内容タイプに応じた表示制御
  const _needText = type === 'text' || type === 'textAndImage' || type === 'textAndTemplate';
  const _needImage = type === 'image' || type === 'textAndImage';
  const _needTemplate = type === 'template' || type === 'textAndTemplate';

  // 共通フックを使用
  const {
    inputImage,
    templateImageUrl,
    getExistingImageUrl,
    handleImageChange,
    handleTemplateImageClear,
    setInputImage,
    setTemplateImageUrl,
  } = useImageHandling({ init, type });

  /**
   * テンプレート選択時の処理
   * 選択されたテンプレートの内容をフォームに反映し、画像の状態を更新する
   *
   * @param template - 選択されたメッセージテンプレート
   */
  const handleSelectTemplate = (template: MessageTemplate) => {
    // フォームにテンプレート内容を設定
    form.setFieldsValue({
      title: template.title || '',
      type: template.broadCastMessage?.type || 'text',
      text: template.broadCastMessage?.text || '',
      altText: template.broadCastMessage?.altText || '',
      tempLabel: template.broadCastMessage?.tempLabel || '',
      tempUri: template.broadCastMessage?.tempUri || '',
    });

    // アップロード済み画像をクリア
    setInputImage(undefined);

    // テンプレートの画像URLを設定
    const imageUrl = template.broadCastMessage?.imageUrl || template.broadCastMessage?.tempImageUrl;
    if (imageUrl) {
      setTemplateImageUrl(imageUrl);
    } else {
      setTemplateImageUrl(null);
    }
  };

  /**
   * 送信対象のラジオボタン変更処理
   * 全員配信とファイルアップロード配信を切り替える
   *
   * @param e - ラジオボタンのイベント
   */
  const onRadioChange = (e: any) => {
    setRadioValue(e.target.value);
  };

  /**
   * CSVアップロード用のUploadProps設定
   * LINE_ID、STORE_ID、MESSAGE列に対応したバリデーション処理
   */
  const uploadProps: UploadProps = {
    accept: 'text/csv',
    action: '/upload.do',
    maxCount: 1,
    showUploadList: lineIds !== null,
    beforeUpload: async (file) => {
      setProcessing(true);
      const csvFile = file.type === 'text/csv';
      if (!csvFile) {
        message.error(`csvファイルのみアップロード可能です`);
        setProcessing(false);
        return Upload.LIST_IGNORE;
      }

      try {
        // FileReaderをPromiseでラップして非同期処理を待つ
        const result = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = (e) => {
            if (!!!e?.target?.result || typeof e?.target?.result !== 'string') {
              reject(new Error('フォーマットが無効です'));
              return;
            }
            resolve(e.target.result.toString());
          };

          reader.onerror = () => {
            reject(new Error('ファイルの読み込みに失敗しました'));
          };

          reader.readAsText(file, 'UTF-8');
        });

        const _csv = csvToArray(result);
        const headers = _csv[0];

        // 列名の重複チェック
        const lineIdColumns = headers.filter(header => header === 'LINE_ID');
        const storeIdColumns = headers.filter(header => header === 'STORE_ID');
        const messageColumns = headers.filter(header => header === 'MESSAGE');

        if (lineIdColumns.length > 1) {
          message.error(`LINE_ID列が複数存在します`);
          setProcessing(false);
          return Upload.LIST_IGNORE;
        }
        if (storeIdColumns.length > 1) {
          message.error(`STORE_ID列が複数存在します`);
          setProcessing(false);
          return Upload.LIST_IGNORE;
        }
        if (messageColumns.length > 1) {
          message.error(`MESSAGE列が複数存在します`);
          setProcessing(false);
          return Upload.LIST_IGNORE;
        }

        // LINE_ID列のインデックスを検索（完全一致）
        const lineIdIndex = headers.findIndex(header => header === 'LINE_ID');
        if (lineIdIndex === -1 || _csv.length < 2) {
          message.error(`フォーマットが無効です。LINE_IDは必須です。`);
          setProcessing(false);
          return Upload.LIST_IGNORE;
        }

        // STORE_IDとMESSAGE列のインデックスを検索（完全一致）
        const storeIdIndex = headers.indexOf('STORE_ID');
        const messageIndex = headers.indexOf('MESSAGE');

        let _lineIds: string[] = [];

        // LINE_IDの取得
        _lineIds = _csv
          .filter(
            (_v, _i) =>
              _i !== 0 && _v[lineIdIndex] !== '' && _v[lineIdIndex] !== undefined
          )
          .map((_v) => _v[lineIdIndex])
          .flat();

        if (_lineIds.length > 500) {
          message.error(`送信するユーザを指定する場合は最大500名です`);
          setProcessing(false);
          return Upload.LIST_IGNORE;
        }

        if (_lineIds.length === 0) {
          message.error(`有効なLINE_IDが見つかりませんでした。`);
          setProcessing(false);
          return Upload.LIST_IGNORE;
        }

        // LINE_IDの重複チェック
        const duplicateLineIds = _lineIds.filter((item, index) => _lineIds.indexOf(item) !== index);
        if (duplicateLineIds.length > 0) {
          message.error(`重複するLINE_IDが存在します。CSVファイルを確認してください。`);
          setProcessing(false);
          return Upload.LIST_IGNORE;
        }

        // STORE_IDとMESSAGEの処理
        if (storeIdIndex !== -1) {
          let customerSpecificDeliverySettings: { [key: string]: { storeId: string; customMessage: string } } = {};

          for (let i = 1; i < _csv.length; i++) {
            const row = _csv[i];
            const lineId = row[lineIdIndex];
            const storeId = row[storeIdIndex]?.trim();

            // STORE_IDの列が存在する場合は、値が必須
            if (!storeId) {
              message.error(`STORE_IDが設定されていない行があります`);
              setProcessing(false);
              return Upload.LIST_IGNORE;
            }

            // MESSAGEは空欄可能
            const customMessage = messageIndex !== -1 ? row[messageIndex]?.trim() || '' : '';

            // LINE_IDをキーにして設定
            customerSpecificDeliverySettings[lineId] = {
              storeId,
              customMessage
            };
          }

          form.setFieldValue('customerSpecificDeliverySettings', customerSpecificDeliverySettings);
        }

        // バリデーションとSTORE_IDの処理が完了した後にLINE_IDsを設定
        form.setFieldValue('sendMCustomerLineIds', _lineIds);

        // 成功時にローディングを解除
        setProcessing(false);
        return false;

      } catch (error) {
        message.error(error instanceof Error ? error.message : 'エラーが発生しました');
        setProcessing(false);
        return Upload.LIST_IGNORE;
      }
    },
    onRemove: () => {
      // LINE IDsをリセット
      form.setFieldValue(
        'sendMCustomerLineIds',
        init?.broadCast?.sendMCustomerLineIds ?? null
      );
      // 配信設定をリセット
      form.setFieldValue(
        'customerSpecificDeliverySettings',
        init?.broadCast?.customerSpecificDeliverySettings ?? null
      );
    },
  };

  /**
   * サンプルCSVデータダウンロード処理
   * 
   * @param format - ダウンロード形式（'csv' | 'xlsx'）
   */
  const onClickSampleData = useCallback(async (format: 'csv' | 'xlsx') => {
    download(
      [
        ['LINE_ID'],
        ['U716f5f7517de3aadglfg2c7d83055910'],
        ['Uff64d02382d4asd7c825c5e7c30c20e0'],
        ['U50d2c782b8fbsdsffg6966caf4aca8e5'],
      ],
      'サンプルデータ',
      format
    );
  }, []);

  /**
   * アップロード済みユーザリストダウンロード処理
   * 
   * @param format - ダウンロード形式（'csv' | 'xlsx'）
   */
  const onClickSendUserIds = useCallback(
    async (format: 'csv' | 'xlsx') => {
      if (!init?.broadCast) return;
      const _title = init.broadCast.title;
      const _customerSettings = init.broadCast.customerSpecificDeliverySettings;

      let headers = ['LINE_ID'];
      let rows: string[][] = [];

      if (_customerSettings) {
        headers = ['LINE_ID', 'STORE_ID', 'MESSAGE'];
        rows = (init.broadCast.sendMCustomerLineIds ?? []).map((_id) => [
          _id,
          _customerSettings[_id]?.storeId ?? '',
          _customerSettings[_id]?.customMessage ?? '',
        ]);
      } else {
        rows = (init.broadCast.sendMCustomerLineIds ?? []).map((_id) => [_id]);
      }

      download(
        [
          headers,
          ...rows,
        ],
        `${_title ?? ''}${_title ? '_' : ''}ユーザリスト`,
        format
      );
    },
    [init?.broadCast]
  );

  /**
   * フォーム送信処理
   * 下書き保存または配信確定を実行
   *
   * @param status - 保存ステータス（'draft' | 'reserved'）
   */
  const onSubmit = async (status: 'draft' | 'reserved') => {
    setLoading(true);
    try {
      const values = form.getFieldsValue();
      
      await _formState.submitNormal(
        {
          ...values,
          broadCast: init?.broadCast,
        },
        status,
        inputImage,
        templateImageUrl
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * 配信タイプ変更時のフォームリセット処理
   * 新規作成時のみフォームをリセットする
   */
  React.useEffect(() => {
    if (deliveryTypeChanged && !init?.broadCast) {
      form.resetFields();
    }
  }, [deliveryTypeChanged, form, init?.broadCast]);

  return (
    <FullscreenLoading isLoading={processing}>
      <Form
        labelWrap
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16, offset: 1 }}
        layout="horizontal"
        style={{ maxWidth: 900, margin: 'auto' }}
        form={form}
        initialValues={init}
        disabled={loading}
      >
        <TemplateSelect onTemplateSelect={handleSelectTemplate} />

        <Form.Item<BroadCastFormType>
          label="タイトル"
          name="title"
          rules={[{ required: true, message: 'タイトルを入力してください' }]}
          tooltip="こちらはお客さまに公開されません"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="storeIds"
          label="送信するアカウント"
          rules={[
            {
              message: '送信する公式アカウントを選択してください',
              type: 'array',
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="送信する公式アカウントを選択してください"
          >
            {loginAccount.stores?.map((_store) => {
              return (
                <Select.Option value={_store.storeId} key={_store.storeId}>
                  {_store.storeName}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>

        <Form.Item
          name="sendAt"
          label="送信日時"
          getValueProps={(i) => ({
            value: !!!i ? undefined : dayjs(i),
          })}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            disabledDate={(d) => d.diff(dayjs(), 'seconds') < 0}
          />
        </Form.Item>

        <Form.Item
          name="type"
          label="送信内容タイプ"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              { value: 'text', label: 'テキストのみ' },
              { value: 'image', label: '画像のみ' },
              { value: 'template', label: 'アクション付き画像のみ（クーポンなど）' },
              { value: 'textAndImage', label: 'テキスト＆画像' },
              { value: 'textAndTemplate', label: 'テキスト＆アクション付き画像' },
            ]}
          />
        </Form.Item>

        <Form.Item
          hidden={!_needText}
          name="text"
          label="テキスト"
          required={_needText}
        >
          <TextArea minLength={1} maxLength={300} />
        </Form.Item>

        <Form.Item
          name="image"
          hidden={!(_needImage || _needTemplate)}
          label="画像"
          required={_needImage || _needTemplate}
        >
          <ImageUpload
            inputImage={inputImage}
            templateImageUrl={templateImageUrl}
            existingImageUrl={getExistingImageUrl()}
            onImageChange={handleImageChange}
            onTemplateImageClear={handleTemplateImageClear}
          />
        </Form.Item>

        <Form.Item
          hidden={!_needTemplate}
          name="tempLabel"
          label="画像上のテキスト"
          required={_needTemplate}
        >
          <Input
            type="text"
            placeholder="クーポンをみる"
            minLength={1}
            maxLength={12}
          />
        </Form.Item>

        <Form.Item
          hidden={!_needTemplate}
          name="altText"
          label="通知に表示されるテキスト"
          required={_needTemplate}
        >
          <Input type="text" maxLength={400} />
        </Form.Item>

        <Form.Item
          hidden={!_needTemplate}
          name="tempUri"
          label="リンク先"
          required={_needTemplate}
        >
          <Input type="url" placeholder="http://mysite.com" maxLength={1000} />
        </Form.Item>

        <Form.Item
          name="sendAll"
          label="送信対象"
          required
          tooltip="送信者を限定する場合のみアップロードが必要です。アップロードがない場合は友達全員に送信されます。"
        >
          <Radio.Group name="sendTarget" onChange={onRadioChange} value={radioValue}>
            <Radio value={true}>全員</Radio>
            <Radio value={false}>ファイルアップロード</Radio>
          </Radio.Group>
        </Form.Item>

        {radioValue === false && (
          <div>
            <Form.Item<BroadCastFormType>
              hidden
              name="customerSpecificDeliverySettings"
            />
            
            <Form.Item
              name="sendMCustomerLineIds"
              label={
                <Flex vertical gap={10} align="end">
                  <DownloadButton
                    name="サンプルデータ"
                    onClick={onClickSampleData}
                    size="small"
                    type="link"
                  />
                </Flex>
              }
            >
              <Flex
                gap={10}
                style={{ maxWidth: '100%', overflow: 'auto', alignItems: 'end' }}
              >
                <Upload.Dragger {...uploadProps} style={{ maxWidth: '400px' }}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    クリックまたはファイルをドラッグ
                  </p>
                  <p
                    className="ant-upload-hint"
                    style={{ whiteSpace: 'pre-wrap', textAlign: 'start' }}
                  >{`※以下の列に対応しています：\n・LINE_ID（必須）：送信したい顧客のLINE ID\n・STORE_ID（任意）：送信するアカウントを指定\n・MESSAGE（任意）：個別のメッセージを指定（空欄の場合は共通メッセージを使用）\n※来店回数、来店期間などの列は無視されます\n※最大500ユーザとなります\n※csv形式に対応しています`}
                  </p>
                </Upload.Dragger>
                <div
                  hidden={
                    (init?.broadCast?.sendMCustomerLineIds?.length ?? 0) === 0
                  }
                >
                  <DownloadButton
                    tooltip="アップロードしたユーザリストをダウンロードできます"
                    onClick={onClickSendUserIds}
                  />
                </div>
              </Flex>
            </Form.Item>
          </div>
        )}

        <Form.Item wrapperCol={{ span: 12, offset: 6 }}>
          <Space>
            <Button
              disabled={init?.broadCast?.status === 'sent'}
              loading={loading}
              type="dashed"
              htmlType="submit"
              onClick={() => onSubmit('draft')}
            >
              下書き保存
            </Button>
            <Button
              disabled={init?.broadCast?.status === 'sent'}
              loading={loading}
              type="primary"
              htmlType="button"
              onClick={() => onSubmit('reserved')}
            >
              確定
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </FullscreenLoading>
  );
};
